import { DateOnly, format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { Banner, DataField, HighlightCallout, SubsectionHeader } from '@if/mui'
import { Card, CardContent, Stack } from '@mui/material'
import { EInteracWireStatus, InteracWire } from '@src/types/interac'
import { ArrowDownToLine, Building2, Mail, MessageSquare, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import InteracMatchResultChip from './interac-match-result'

interface InteracTransferCardProps {
  wire: InteracWire
}

const SENDER_EMAIL_SUBJECT = encodeURIComponent(
  'IMPORTANT: Paiement reçu – information de dossier requise / Payment Received – File Information Required',
)

const InteracTransferCard = ({ wire }: InteracTransferCardProps) => {
  const { t } = useTranslation()
  const needsReview = wire.status === EInteracWireStatus.InProcess

  return (
    <Card>
      <SubsectionHeader
        icon={ArrowDownToLine}
        title={t('interacWire.transfer.title')}
        subtitle={t('interacWire.transfer.arrivedOn', { date: format(new DateOnly(wire.receivedOn), 'PPP') })}
        action={<InteracMatchResultChip systemMatchingResult={wire.systemMatchingResult} />}
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack spacing={2.5}>
          {needsReview && (
            <Banner severity="warning" title={t('interacWire.transfer.needsReviewTitle')}>
              {t('interacWire.transfer.needsReview')}
            </Banner>
          )}

          <HighlightCallout
            subjectLabel={t('interacWire.transfer.amountReceived')}
            subject={t('interacWire.transfer.amountReceivedCaption')}
            value={formatCurrency(wire.amount)}
          />

          <Stack direction={{ xs: 'row' }} flexWrap="wrap" gap={2}>
            <DataField icon={User} label={t('interacWire.transfer.sender')} value={wire.sentBy} sx={{ flexGrow: 1 }} />
            <DataField
              icon={Mail}
              label={t('interacWire.transfer.senderEmail')}
              value={wire.senderEmail}
              href={`mailto:${wire.senderEmail}?subject=${SENDER_EMAIL_SUBJECT}`}
              sx={{ flexGrow: 1 }}
            />
            <DataField
              icon={Building2}
              label={t('interacWire.transfer.originatingBank')}
              value={wire.bankName}
              sx={{ flexGrow: 1 }}
            />
            <DataField
              icon={MessageSquare}
              label={t('interacWire.transfer.memo')}
              value={wire.message}
              sx={{ flexGrow: 1 }}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default InteracTransferCard
